// js/modules/ChatWidgetManager.js (VERSÃO COMPLETA E ATUALIZADA)

import supabase from '../supabase-config.js';
import * as messagesApi from '../api/messagesApi.js';
import { sanitizeAndFormatText } from '../utils.js';
import { resolveImagePath } from '../utils/pathUtils.js';
import 'emoji-picker-element';

const ICONS = {
    emoji: `<svg viewBox="0 0 24 24"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"></path></svg>`,
    send: `<svg viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path></svg>`
};

const ChatWidgetManager = {
    container: null,
    openChats: new Map(),
    currentUserId: null,

    init(userId) {
        this.currentUserId = userId;
        this.container = document.getElementById('chat-widget-container');
        if (!this.container) {
            console.error("Container do Chat Widget não encontrado!");
            return;
        }

        this.container.addEventListener('click', e => {
            const windowEl = e.target.closest('.chat-window');
            if (!windowEl) return;

            const convId = windowEl.dataset.conversationId;
            if (e.target.closest('.chat-window-header')) {
                this.toggleMinimize(convId);
            }
            if (e.target.closest('.btn-close-chat')) {
                this.closeChat(convId);
            }
            if (e.target.closest('.btn-send-chat')) {
                const input = windowEl.querySelector('.chat-window-input input');
                this.sendMessage(convId, input);
            }
        });

        this.container.addEventListener('keypress', e => {
            if (e.key === 'Enter' && e.target.matches('.chat-window-input input')) {
                const windowEl = e.target.closest('.chat-window');
                const convId = windowEl.dataset.conversationId;
                this.sendMessage(convId, e.target);
            }
        });
    },

    async openChat(friendId, friendName, friendAvatar) {
        if (this.openChats.size >= 3) {
            alert("Você só pode ter 3 conversas abertas ao mesmo tempo.");
            return;
        }

        try {
            const convId = await messagesApi.findOrCreateConversation(friendId);

            if (this.openChats.has(convId)) {
                const chat = this.openChats.get(convId);
                chat.element.classList.remove('minimized');
                chat.element.querySelector('input').focus();
                return;
            }

            const windowEl = this.createChatWindow(convId, friendName, friendAvatar);
            this.container.appendChild(windowEl);
            
            const channel = this.subscribeToMessages(convId);
            this.openChats.set(convId, { element: windowEl, channel });
            
            this.setupEmojiPicker(convId);
            await this.loadMessageHistory(convId);

        } catch (error) {
            console.error("Erro ao abrir chat:", error);
            alert("Não foi possível iniciar a conversa.");
        }
    },

    createChatWindow(convId, name, avatar) {
        const div = document.createElement('div');
        div.className = 'chat-window';
        div.dataset.conversationId = convId;
        
        div.innerHTML = `
            <header class="chat-window-header">
                <img src="${resolveImagePath(avatar)}" alt="${name}">
                <strong>${name}</strong>
                <div class="chat-window-actions">
                    <button class="btn-minimize-chat">-</button>
                    <button class="btn-close-chat">×</button>
                </div>
            </header>
            <div class="chat-window-messages">
                <div class="loading-spinner"></div>
            </div>
            <div class="chat-window-input">
                <button class="btn-chat-icon btn-emoji-chat" title="Inserir emoji">${ICONS.emoji}</button>
                <input type="text" placeholder="Digite uma mensagem...">
                <button class="btn-chat-icon btn-send-chat" title="Enviar">${ICONS.send}</button>
            </div>
        `;
        return div;
    },

    setupEmojiPicker(convId) {
        const chat = this.openChats.get(convId);
        if (!chat) return;

        const emojiBtn = chat.element.querySelector('.btn-emoji-chat');
        const input = chat.element.querySelector('.chat-window-input input');
        
        const picker = document.createElement('emoji-picker');
        picker.style.display = 'none';
        chat.element.appendChild(picker);

        picker.addEventListener('emoji-click', e => {
            this.insertTextAtCursor(input, e.detail.unicode);
        });

        emojiBtn.addEventListener('click', e => {
            e.stopPropagation();
            picker.style.display = picker.style.display === 'none' ? 'block' : 'none';
        });

        const clickOutsideHandler = e => {
            if (!picker.contains(e.target) && e.target !== emojiBtn) {
                picker.style.display = 'none';
            }
        };

        document.addEventListener('click', clickOutsideHandler);
        chat.clickOutsideHandler = clickOutsideHandler;
    },

    insertTextAtCursor(inputElement, text) {
        const start = inputElement.selectionStart;
        const end = inputElement.selectionEnd;
        inputElement.value = inputElement.value.slice(0, start) + text + inputElement.value.slice(end);
        inputElement.selectionStart = inputElement.selectionEnd = start + text.length;
        inputElement.focus();
    },

    async loadMessageHistory(convId) {
        const chat = this.openChats.get(convId);
        const messagesContainer = chat.element.querySelector('.chat-window-messages');
        const messages = await messagesApi.fetchMessages(convId);
        
        messagesContainer.innerHTML = messages.map(msg => this.createMessageBubbleHTML(msg)).join('');
        this.scrollToBottom(messagesContainer);
    },

    async sendMessage(convId, inputElement) {
        const content = inputElement.value.trim();
        if (!content) return;

        inputElement.disabled = true;
        try {
            await messagesApi.sendMessage(convId, content);
            inputElement.value = '';
        } catch (error) {
            console.error("Erro ao enviar mensagem via widget:", error);
        } finally {
            inputElement.disabled = false;
            inputElement.focus();
        }
    },

    subscribeToMessages(convId) {
        const channel = supabase
            .channel(`chat-widget:${convId}`)
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${convId}` },
                (payload) => {
                    const chat = this.openChats.get(convId);
                    if (chat) {
                        const messagesContainer = chat.element.querySelector('.chat-window-messages');
                        messagesContainer.insertAdjacentHTML('beforeend', this.createMessageBubbleHTML(payload.new));
                        this.scrollToBottom(messagesContainer);
                    }
                }
            )
            .subscribe();
        return channel;
    },

    createMessageBubbleHTML(message) {
            const isSent = message.sender_id === this.currentUserId;
            const formattedContent = sanitizeAndFormatText(message.content);

            // Formata a data e hora completas
            const timestamp = new Date(message.created_at).toLocaleString('pt-BR', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });

            // O timestamp agora vai DENTRO da bolha para ser posicionado por ela
            return `
                <div class="message-bubble ${isSent ? 'sent' : 'received'}">
                    ${formattedContent}
                    <span class="message-timestamp">${timestamp}</span>
                </div>
            `;
        },

    toggleMinimize(convId) {
        const chat = this.openChats.get(convId);
        if (chat) {
            chat.element.classList.toggle('minimized');
        }
    },

    closeChat(convId) {
        const chat = this.openChats.get(convId);
        if (chat) {
            supabase.removeChannel(chat.channel);
            if (chat.clickOutsideHandler) {
                document.removeEventListener('click', chat.clickOutsideHandler);
            }
            chat.element.querySelector('emoji-picker')?.remove();
            chat.element.remove();
            this.openChats.delete(convId);
        }
    },

    scrollToBottom(container) {
        container.scrollTop = container.scrollHeight;
    }
};

export default ChatWidgetManager;